import React from 'react';
import Icon from '../../../components/AppIcon';
import ReportRadarChart from './ReportRadarChart';

const HistoricalTrend = ({ historicalTrend = [], deltaVsPrevious, radarData }) => {
  // First interview — show motivational card instead of blank space
  if (!historicalTrend || historicalTrend.length < 2) {
    return (
      <div className="bg-gradient-to-br from-slate-50 to-emerald-50/30 border border-slate-100 rounded-[2rem] p-6 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-xl bg-emerald-50 text-emerald-500 border border-emerald-100">
            <Icon name="TrendingUp" size={16} />
          </div>
          <div>
            <h3 className="text-xs font-black text-slate-900tracking-[0.15em]">Your Progress</h3>
            <p className="text-[10px] text-slate-400 font-bold mt-0.5">Track your growth over time</p>
          </div>
        </div>
        <div className="text-center py-6 space-y-3">
          <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto">
            <Icon name="Award" size={24} className="text-emerald-500" />
          </div>
          <h4 className="text-sm font-black text-slate-800">This is your baseline!</h4>
          <p className="text-[11px] text-slate-500 font-medium max-w-sm mx-auto leading-relaxed">
            Complete 2+ interviews to unlock your personal progress chart. We'll track every parameter — so you can see exactly where you're improving.
          </p>
          <a href="/interview-setup" className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 text-white rounded-xl text-[10px] font-blacktracking-wider hover:bg-slate-800 transition-colors mt-2">
            <Icon name="Play" size={10} />
            Take Another Interview
          </a>
        </div>
      </div>
    );
  }

  // Reverse so oldest is first (for left-to-right chart)
  const entries = [...historicalTrend].reverse();
  const scores = entries.map(e => e.overallScore || 0);
  const maxScore = Math.max(...scores, 100);
  const minScore = Math.min(...scores, 0);

  // SVG sparkline dimensions
  const width = 300;
  const height = 80;
  const padding = 10;

  const points = scores.map((s, i) => ({
    x: padding + (i / (scores.length - 1)) * (width - padding * 2),
    y: height - padding - ((s - Math.max(minScore - 10, 0)) / (maxScore - Math.max(minScore - 10, 0) + 10)) * (height - padding * 2),
    score: s,
  }));

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaD = pathD + ` L ${points[points.length - 1].x} ${height - padding} L ${points[0].x} ${height - padding} Z`;

  // Overall delta
  const overallDelta = deltaVsPrevious?.overall;
  const isImproving = overallDelta && overallDelta > 0;
  const isFirst = historicalTrend.length <= 1;

  // Extract unique parameter names from the latest entry
  const latestParams = entries.length > 0 ? entries[entries.length - 1].parameterScores || {} : {};
  const prevParams = entries.length > 1 ? entries[entries.length - 2].parameterScores || {} : {};

  const paramTrends = Object.keys(latestParams).map(name => ({
    name,
    current: latestParams[name] || 0,
    previous: prevParams[name] || 0,
    delta: (latestParams[name] || 0) - (prevParams[name] || 0),
  }));

  // Sort by absolute delta (biggest changes first)
  paramTrends.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  const getColor = (delta) => {
    if (delta > 5) return 'text-emerald-500';
    if (delta > 0) return 'text-emerald-400';
    if (delta === 0) return 'text-slate-400';
    if (delta > -5) return 'text-amber-500';
    return 'text-red-500';
  };

  return (
    <div className="bg-white border border-slate-100 rounded-[2rem] p-6 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-emerald-50 text-emerald-500 border border-emerald-100">
            <Icon name="TrendingUp" size={16} />
          </div>
          <div>
            <h3 className="text-xs font-black text-slate-900tracking-[0.15em]">Your Progress</h3>
            <p className="text-[10px] text-slate-400 font-bold mt-0.5">Last {entries.length} interviews</p>
          </div>
        </div>

        {/* Overall delta badge */}
        {overallDelta !== undefined && overallDelta !== 0 && (
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border ${
            isImproving
              ? 'bg-emerald-50 border-emerald-100 text-emerald-600'
              : 'bg-red-50 border-red-100 text-red-600'
          }`}>
            <Icon name={isImproving ? 'TrendingUp' : 'TrendingDown'} size={14} />
            <span className="text-sm font-black">
              {overallDelta > 0 ? '+' : ''}{overallDelta} vs last time
            </span>
          </div>
        )}
      </div>

      {/* 2-column layout: Left = sparkline + skill changes, Right = radar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column — sparkline + skill deltas */}
        <div className="lg:col-span-2 space-y-5">
          {/* Sparkline chart */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 relative group">
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-20" preserveAspectRatio="none">
              {/* Area fill */}
              <path d={areaD} fill="url(#sparkGradient)" />
              {/* Line */}
              <path d={pathD} fill="none" stroke="#10E58F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
              
              <defs>
                <linearGradient id="sparkGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10E58F" stopOpacity="0.15" />
                  <stop offset="100%" stopColor="#10E58F" stopOpacity="0" />
                </linearGradient>
              </defs>
            </svg>

            {/* Overlay for Points & Labels */}
            <div className="absolute inset-4 pointer-events-none">
              {points.map((p, i) => (
                <div 
                  key={i} 
                  className="absolute group-hover:block" 
                  style={{ 
                    left: `${(p.x / width) * 100}%`, 
                    top: `${(p.y / height) * 100}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  <div 
                    className={`rounded-full border-2 border-emerald-500 shadow-sm transition-transform duration-300 ${
                      i === points.length - 1 ? 'w-2.5 h-2.5 bg-emerald-500 scale-125' : 'w-2 h-2 bg-white'
                    }`}
                  />
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 text-[9px] font-black text-slate-500 whitespace-nowrap bg-white/80 backdrop-blur-[2px] px-1 rounded tabular-nums">
                    {p.score}
                  </span>
                </div>
              ))}
            </div>

            {/* Date labels */}
            <div className="flex justify-between mt-1.5 px-2">
              {entries.map((e, i) => (
                <span key={i} className="text-[8px] font-bold text-slate-400 tabular-nums">
                  {new Date(e.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              ))}
            </div>
          </div>

          {/* Parameter-level trends */}
          {paramTrends.length > 0 && (() => {
            const mid = Math.ceil(paramTrends.length / 2);
            const leftCol = paramTrends.slice(0, mid);
            const rightCol = paramTrends.slice(mid);

            const SkillRow = ({ pt, isLast }) => (
              <div className={`flex items-center justify-between py-2.5 px-1 ${!isLast ? 'border-b border-slate-100' : ''}`}>
                <span className="text-[11px] font-bold text-slate-700">{pt.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-400 font-medium tabular-nums">
                    {pt.previous} → <span className="text-slate-700 font-bold">{pt.current}</span>
                  </span>
                  {pt.delta !== 0 && (
                    <span className={`text-[10px] font-bold flex items-center gap-0.5 tabular-nums ${getColor(pt.delta)}`}>
                      <Icon name={pt.delta > 0 ? 'ArrowUpRight' : 'ArrowDownRight'} size={10} />
                      {pt.delta > 0 ? '+' : ''}{pt.delta}
                    </span>
                  )}
                  {pt.delta === 0 && (
                    <span className="text-[10px] font-bold text-slate-400 tabular-nums">—</span>
                  )}
                </div>
              </div>
            );

            return (
              <div>
                <p className="text-[9px] font-black text-slate-400tracking-widest mb-3">Skill Changes vs Last Interview</p>
                <div className="grid grid-cols-2 gap-x-6">
                  <div className="bg-slate-50 rounded-xl px-3 border border-slate-100">
                    {leftCol.map((pt, idx) => (
                      <SkillRow key={idx} pt={pt} isLast={idx === leftCol.length - 1} />
                    ))}
                  </div>
                  <div className="bg-slate-50 rounded-xl px-3 border border-slate-100">
                    {rightCol.map((pt, idx) => (
                      <SkillRow key={idx} pt={pt} isLast={idx === rightCol.length - 1} />
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Right column — Skill Radar */}
        {radarData && radarData.length > 0 && (
          <div className="lg:col-span-1 flex flex-col items-center justify-center">
            <p className="text-[9px] font-black text-slate-400tracking-widest mb-4">Skill Radar</p>
            <div className="flex items-center justify-center min-h-[200px]">
              <ReportRadarChart data={radarData} size={200} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoricalTrend;
